import { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import { trackStoryUpload } from '../utils/analytics'
import { invalidateSubscriptionCache } from './useSubscription'

// File validation constants
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp']

// Parallelization settings
const MAX_CONCURRENT_UPLOADS = 5 // Maximum parallel uploads

export function useImageUpload() {
  const { t } = useTranslation('errors')
  const [uploadQueue, setUploadQueue] = useState([])
  const uploadControllers = useRef(new Map())

  // Translate backend error messages to localized versions
  const translateBackendError = useCallback((errorMessage) => {
    // Check for story limit error pattern
    const storyLimitMatch = errorMessage.match(/Story limit reached\. You have used ([\d.]+) of ([\d.]+) stories/)
    if (storyLimitMatch) {
      const [, used, limit] = storyLimitMatch
      return t('storyLimitReached', { used, limit })
    }

    // Return original message if no translation found
    return errorMessage
  }, [t])

  // Validate file before upload
  const validateFile = (file) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File "${file.name}" exceeds 10MB limit`
      }
    }

    // Check file type
    const fileType = file.type.toLowerCase()
    const fileName = file.name.toLowerCase()
    const hasValidType = ALLOWED_TYPES.includes(fileType)
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext))

    if (!hasValidType || !hasValidExtension) {
      return {
        valid: false,
        error: `File "${file.name}" is not a supported image format (JPEG, PNG, or WebP only)`
      }
    }

    return { valid: true }
  }

  // Create preview URL for image
  const createPreview = (file) => {
    try {
      return URL.createObjectURL(file)
    } catch (error) {
      console.error('Failed to create preview:', error)
      return null
    }
  }

  // Upload a single file
  const uploadFile = useCallback(async (queueItem, accountId) => {
    const { id, file } = queueItem

    try {
      // Create abort controller for this upload
      const controller = new AbortController()
      uploadControllers.current.set(id, controller)

      // Update status to uploading
      setUploadQueue(prev => prev.map(item =>
        item.id === id
          ? { ...item, status: 'uploading', progress: 0, error: null }
          : item
      ))

      // Create FormData
      const formData = new FormData()
      formData.append('file', file)
      formData.append('instagram_account_id', accountId)

      // Upload with progress tracking
      const response = await api.uploadImage(formData, {
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadQueue(prev => prev.map(item =>
            item.id === id
              ? { ...item, progress }
              : item
          ))
        }
      })

      // Update status to processing (AI analysis)
      setUploadQueue(prev => prev.map(item =>
        item.id === id
          ? { ...item, status: 'processing', progress: 100, imageId: response.image_id, processingStartTime: Date.now() }
          : item
      ))

      // Poll status endpoint until image is analyzed
      const imageId = response.image_id
      const maxAttempts = 90 // Max 180 seconds (90 attempts * 2 seconds)
      const pollInterval = 2000 // Poll every 2 seconds
      let attempts = 0
      let imageAnalyzed = false

      while (!imageAnalyzed && attempts < maxAttempts && !controller.signal.aborted) {
        attempts++

        try {
          // Wait 2 seconds before checking status (with abort support)
          await new Promise((resolve, reject) => {
            const timeoutId = setTimeout(resolve, pollInterval)
            controller.signal.addEventListener('abort', () => {
              clearTimeout(timeoutId)
              reject(new DOMException('Aborted', 'AbortError'))
            })
          })

          // Update processing time elapsed
          setUploadQueue(prev => prev.map(item =>
            item.id === id
              ? { ...item, processingElapsed: Date.now() - item.processingStartTime }
              : item
          ))

          // Check if aborted during wait
          if (controller.signal.aborted) break

          // Check image status
          const statusResponse = await api.getImageStatus(imageId, {
            instagram_account_id: accountId,
            signal: controller.signal
          })

          // Check if analysis is complete
          if (statusResponse.status !== 'processing') {
            imageAnalyzed = true

            // Update status to complete with full image data (only if not aborted)
            if (!controller.signal.aborted) {
              setUploadQueue(prev => prev.map(item =>
                item.id === id
                  ? {
                      ...item,
                      status: 'complete',
                      imageId: imageId,
                      imageData: statusResponse.image
                    }
                  : item
              ))
              // Track successful upload
              trackStoryUpload(1, 'image')
              // Invalidate subscription cache to force immediate quota refresh
              invalidateSubscriptionCache(accountId).catch(err =>
                console.warn('Failed to refresh subscription cache:', err)
              )
            }
          }
        } catch (error) {
          // If aborted, exit polling loop
          if (error.name === 'AbortError') {
            break
          }
          // If polling fails, continue waiting (don't fail the upload)
          console.warn(`Status polling attempt ${attempts} failed:`, error.message)
        }
      }

      // If max attempts reached without completion, still mark as complete
      // (ImageAnalyzer might still be running, user can check Approval page)
      if (!imageAnalyzed && !controller.signal.aborted) {
        const timeoutSeconds = (maxAttempts * pollInterval) / 1000
        console.warn(`Image ${imageId} analysis status unknown after ${maxAttempts} attempts (${timeoutSeconds}s). Analysis may still be running.`)
        setUploadQueue(prev => prev.map(item =>
          item.id === id
            ? { ...item, status: 'complete', imageId: imageId, warning: 'Analysis taking longer than expected. Check Approval page.' }
            : item
        ))
        // Track successful upload (even if analysis status unknown)
        trackStoryUpload(1, 'image')
        // Invalidate subscription cache to force immediate quota refresh
        invalidateSubscriptionCache(accountId).catch(err =>
          console.warn('Failed to refresh subscription cache:', err)
        )
      }

      // Clean up controller
      uploadControllers.current.delete(id)

      return response
    } catch (error) {
      // Handle errors
      let errorMessage = 'Upload failed'

      if (error.name === 'AbortError') {
        errorMessage = 'Upload cancelled'
      } else if (error.response?.data?.error) {
        // Translate backend error message
        errorMessage = translateBackendError(error.response.data.error)
      } else if (error.message) {
        errorMessage = error.message
      }

      setUploadQueue(prev => prev.map(item =>
        item.id === id
          ? { ...item, status: 'error', error: errorMessage, progress: 0 }
          : item
      ))

      // Clean up controller
      uploadControllers.current.delete(id)

      throw error
    }
  }, [])

  // Process uploads in batches of MAX_CONCURRENT_UPLOADS
  const processBatch = useCallback(async (batch, accountId) => {
    // Upload all files in this batch in parallel
    const uploadPromises = batch.map(item =>
      uploadFile(item, accountId).catch(error => {
        console.error(`Failed to upload ${item.file.name}:`, error)
        return null // Return null for failed uploads so Promise.allSettled continues
      })
    )

    // Wait for all uploads in this batch to complete
    await Promise.allSettled(uploadPromises)
  }, [uploadFile])

  // Add files to upload queue
  const uploadFiles = useCallback(async (files, accountId) => {
    const validFiles = []
    const errors = []

    // Validate all files
    for (const file of files) {
      const validation = validateFile(file)
      if (validation.valid) {
        validFiles.push(file)
      } else {
        errors.push(validation.error)
      }
    }

    // Show validation errors
    if (errors.length > 0) {
      errors.forEach(error => {
        console.error('File validation error:', error)
        // You could show a toast notification here
      })
    }

    // Add valid files to queue
    if (validFiles.length > 0) {
      const newQueueItems = validFiles.map(file => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: createPreview(file),
        status: 'pending',
        progress: 0,
        error: null,
        imageId: null
      }))

      setUploadQueue(prev => [...prev, ...newQueueItems])

      // Process uploads in batches for parallelization
      console.log(`Starting parallel uploads: ${newQueueItems.length} files, max ${MAX_CONCURRENT_UPLOADS} concurrent`)

      for (let i = 0; i < newQueueItems.length; i += MAX_CONCURRENT_UPLOADS) {
        const batch = newQueueItems.slice(i, i + MAX_CONCURRENT_UPLOADS)
        console.log(`Processing batch ${Math.floor(i / MAX_CONCURRENT_UPLOADS) + 1}: ${batch.length} files`)
        await processBatch(batch, accountId)
      }

      console.log('All uploads completed')
    }
  }, [processBatch])

  // Remove item from queue
  const removeFromQueue = useCallback((id) => {
    // Cancel upload if in progress
    const controller = uploadControllers.current.get(id)
    if (controller) {
      controller.abort()
      uploadControllers.current.delete(id)
    }

    // Remove from queue and revoke preview URL
    setUploadQueue(prev => {
      const item = prev.find(item => item.id === id)
      if (item?.preview) {
        URL.revokeObjectURL(item.preview)
      }
      return prev.filter(item => item.id !== id)
    })
  }, [])

  // Retry failed upload
  const retryUpload = useCallback(async (id, accountId) => {
    const item = uploadQueue.find(item => item.id === id)
    if (item && item.status === 'error') {
      await uploadFile(item, accountId)
    }
  }, [uploadQueue, uploadFile])

  // Clear completed uploads
  const clearCompleted = useCallback(() => {
    setUploadQueue(prev => {
      // Revoke preview URLs for completed items
      prev.filter(item => item.status === 'complete').forEach(item => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview)
        }
      })
      // Keep only non-completed items
      return prev.filter(item => item.status !== 'complete')
    })
  }, [])

  // Cancel all uploads
  const cancelAllUploads = useCallback(() => {
    uploadControllers.current.forEach(controller => controller.abort())
    uploadControllers.current.clear()

    setUploadQueue(prev => prev.map(item => {
      if (item.status === 'uploading' || item.status === 'processing') {
        return { ...item, status: 'error', error: 'Upload cancelled', progress: 0 }
      }
      return item
    }))
  }, [])

  // Get upload statistics
  const getStats = useCallback(() => {
    const pending = uploadQueue.filter(item => item.status === 'pending').length
    const uploading = uploadQueue.filter(item => item.status === 'uploading').length
    const processing = uploadQueue.filter(item => item.status === 'processing').length
    const complete = uploadQueue.filter(item => item.status === 'complete').length
    const error = uploadQueue.filter(item => item.status === 'error').length
    const total = uploadQueue.length

    return {
      pending,
      uploading,
      processing,
      complete,
      error,
      total,
      inProgress: uploading + processing,
      hasActive: uploading + processing > 0,
      hasErrors: error > 0,
      hasCompleted: complete > 0
    }
  }, [uploadQueue])

  return {
    uploadQueue,
    uploadFiles,
    removeFromQueue,
    retryUpload,
    clearCompleted,
    cancelAllUploads,
    getStats,
    validateFile
  }
}